import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Ship, Globe2, ShieldCheck, Boxes, Anchor,
  Compass, Waves, CheckCircle2,
} from "lucide-react";
import { Page } from "@/components/Page";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/Button";
import { StatCounter } from "@/components/StatCounter";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, RevealStagger, StaggerItem } from "@/components/Reveal";
import { WorldMap } from "@/components/WorldMap";
import { IMAGES, SERVICES, HERO_STATS, INDUSTRIES, COMPANY } from "@/data/site";

const FEATURED = ["freight-forwarding", "vessel-chartering", "customs-clearing", "project-cargo"];

const Home = () => {
  const featuredServices = FEATURED.map((s) => SERVICES.find((x) => x.slug === s));

  return (
    <Page>
      <SEO
        title="Global Maritime Logistics"
        description="Speed Shipping Agencies — integrated freight forwarding, vessel chartering, customs solutions and logistics services across international markets. Trusted since 2002."
      />

      {/* HERO */}
      <section data-testid="home-hero" className="relative isolate min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <motion.img
            src={IMAGES.heroPrimary}
            alt="Cargo vessel navigating international waters"
            className="h-full w-full object-cover"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: "easeOut" }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="ssa-container relative w-full pt-32 pb-20 lg:pt-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10" style={{ background: "var(--ssa-gold)" }} />
                  <span className="eyebrow text-turquoise">Maritime Logistics · Since {COMPANY.since}</span>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 data-testid="home-hero-headline" className="text-white text-[2.5rem] sm:text-6xl lg:text-7xl leading-[1.04]">
                  Global Logistics.
                  <br />
                  <span className="text-turquoise">Maritime Excellence.</span>
                  <br />
                  Trusted Since 2002.
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-7 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
                  Delivering integrated freight forwarding, vessel chartering, customs solutions, and
                  logistics services across international markets.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-9 flex flex-col sm:flex-row gap-4">
                  <Button to="/services" variant="light" size="xl" data-testid="home-hero-primary-cta">
                    Explore Our Services <ArrowRight className="h-5 w-5" />
                  </Button>
                  <Button to="/global-network" variant="outlineLight" size="xl" data-testid="home-hero-secondary-cta">
                    Our Global Network <Globe2 className="h-5 w-5" />
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Stats bar */}
          <Reveal delay={0.3}>
            <div
              data-testid="home-hero-stats"
              className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden glass-dark"
            >
              {HERO_STATS.map((s, i) => (
                <div key={i} className="p-6 lg:p-7">
                  <StatCounter value={s.value} suffix={s.suffix} label={s.label} dark separator={s.plain ? "" : ","} testId={`hero-stat-${i}`} />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-navy text-white">
        <div className="ssa-container py-6">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-white/70">
            {[
              { icon: Anchor, label: "Vessel Chartering" },
              { icon: Globe2, label: "International Agent Network" },
              { icon: ShieldCheck, label: "Licensed Customs Clearing" },
              { icon: Boxes, label: "Project Cargo Expertise" },
              { icon: Compass, label: "End-to-End Supply Chain" },
            ].map((it, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm">
                <it.icon className="h-4 w-4 text-turquoise" />
                <span className="tracking-wide">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO / WHO WE ARE */}
      <section className="py-24 lg:py-32 ocean-sheen relative">
        <div className="ssa-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionHeading
                eyebrow="Who We Are"
                title="A trusted maritime partner across international trade corridors"
                intro="Established in 2002, Speed Shipping Agencies delivers integrated logistics and maritime solutions for importers, exporters, shipping lines and project stakeholders. Our discipline, transparency and global agent network make complex movements simple."
              />
              <RevealStagger className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  "Two decades of maritime expertise",
                  "Curated international agent network",
                  "End-to-end, door-to-door solutions",
                  "Specialist project & heavy-lift cargo",
                ].map((t) => (
                  <StaggerItem key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-turquoise mt-0.5 shrink-0" />
                    <span className="text-sm text-[color:var(--ssa-text-primary)]">{t}</span>
                  </StaggerItem>
                ))}
              </RevealStagger>
              <Reveal delay={0.1}>
                <div className="mt-9">
                  <Button to="/about" variant="primary" size="lg">
                    About Speed Shipping Agencies <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(8,28,58,0.22)]">
                  <img src={IMAGES.portNight} alt="Container terminal operations at night" className="w-full h-[460px] object-cover" />
                  <div className="absolute inset-0 card-overlay" />
                </div>
                <div className="absolute -bottom-6 -left-6 glass-light rounded-2xl p-6 w-52 shadow-[0_20px_50px_rgba(8,28,58,0.18)] hidden sm:block">
                  <Waves className="h-6 w-6 text-royal" />
                  <div className="mt-3 font-display text-2xl text-navy">24 / 7</div>
                  <div className="text-xs text-secondary-ssa mt-1">Operational coordination across time zones</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 lg:py-32">
        <div className="ssa-container">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <SectionHeading
              eyebrow="Our Services"
              title="Integrated logistics, engineered for reliability"
              intro="From customs clearing to vessel chartering, every service is delivered with operational precision and full accountability."
            />
            <Reveal>
              <Button to="/services" variant="outline" size="lg" className="shrink-0">
                View all services <ArrowUpRight className="h-4 w-4" />
              </Button>
            </Reveal>
          </div>

          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredServices.map((s) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group block h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-7 transition-shadow duration-300 hover:shadow-[0_22px_55px_rgba(8,28,58,0.14)] hover:-translate-y-1 will-change-transform"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-turquoise">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl text-navy">{s.name}</h3>
                  <p className="mt-3 text-sm text-secondary-ssa leading-relaxed">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-royal">
                    Learn more
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* GLOBAL NETWORK */}
      <section className="py-24 lg:py-32 bg-ink relative overflow-hidden">
        <div className="ssa-container relative">
          <div className="max-w-3xl mb-12">
            <SectionHeading
              dark
              eyebrow="Global Network"
              title="Connected across Asia, the Middle East, Africa & Europe"
              intro="A coordinated network of international agents and maritime partners enables seamless movement across the world's principal trade corridors."
            />
          </div>
          <Reveal delay={0.1}>
            <WorldMap />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <Button to="/global-network" variant="outlineLight" size="lg">
                Explore our global reach <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="py-24 lg:py-32">
        <div className="ssa-container">
          <SectionHeading
            align="center"
            eyebrow="Industries We Serve"
            title="Specialist logistics across critical sectors"
            intro="Tailored capabilities for the industries that move global trade forward."
            className="mb-14"
          />
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6">
            {INDUSTRIES.slice(0, 8).map((ind) => (
              <StaggerItem key={ind.slug}>
                <Link
                  to="/industries"
                  className="group flex flex-col items-start gap-4 h-full rounded-2xl border border-[color:var(--ssa-border-subtle)] bg-white p-6 transition-colors duration-300 hover:border-[rgba(30,79,168,0.35)]"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(8,28,58,0.05)] text-navy group-hover:bg-navy group-hover:text-turquoise transition-colors">
                    <ind.icon className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-medium text-navy">{ind.name}</span>
                </Link>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CTA */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.vesselDock} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="ssa-container py-24 lg:py-32 text-center">
          <Reveal>
            <h2 className="text-white text-3xl sm:text-4xl lg:text-5xl max-w-3xl mx-auto leading-tight">
              Precision. Reliability. Global Reach.
            </h2>
            <p className="mt-6 text-white/80 max-w-xl mx-auto">
              Partner with a maritime logistics company trusted across international trade and shipping networks.
            </p>
            <div className="mt-9 flex justify-center">
              <Button to="/contact" variant="light" size="xl">
                Contact Our Team <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
};

export default Home;
